<template>
  <div ref="LvDropdownTreeSelect" class="lv-dropdown-tree-select">
    <LvText v-if="title" h6 bold class="lv-dropdown-tree-select__title">
      {{ $t(title) }}<LvText v-if="required" red h5>*</LvText>
    </LvText>
    <ValidationProvider
      v-slot="{ errors }"
      class="lv-dropdown-tree-select__content-wrap"
      :rules="rules"
    >
      <input type="hidden" :value="value" />
      <div v-click-outside="$_onCloseMenu">
        <div
          class="lv-dropdown-tree-select__content"
          :class="{
            outline: outline,
            narrow: narrow,
            wide: wide,
            disabled: _isContentDisabled,
            'lv-dropdown-tree-select__content--error': errors.length > 0,
          }"
        >
          <button class="input-button" @click="$_onClick">
            <LvText h5 class="select">{{ _showText }}</LvText>
            <i
              v-if="_isDisplayCancelIcon"
              class="cancel-icon icon-cancel"
              @click.stop="$_onCancel"
            />
          </button>
          <i
            v-if="outline || _isContentDisabled"
            class="icon trailing-icon outline icon-chevron-up"
            :class="{ show: dropdownMenuShow }"
          />
          <i
            v-else-if="!outline && !dropdownMenuShow"
            class="icon trailing-icon icon-arrow-drop-down-circle"
          />
          <i
            v-else-if="!outline && dropdownMenuShow"
            class="icon trailing-icon icon-arrow-drop-up-circle"
          />
          <transition mode="out-in">
            <div v-if="dropdownMenuShow" class="lv-dropdown-menu" :class="{ 'drop-up': isDropUp }">
              <perfect-scrollbar class="ps lv-dropdown-scrollable-content">
                <WsTextChip001
                  v-if="remindText"
                  thin
                  :textLabel="remindText"
                  :color="remindTextColor"
                  :leadingIcon="remindTextIcon"
                  class="pl-12 pt-8"
                />
                <div v-if="showAllOption">
                  <div class="classification-subtitle">
                    <div
                      class="dropdown-menu-item all-option"
                      :class="{ selected: _isAllSelected }"
                      @click.stop="$_onSelectAll"
                    >
                      <LvText h5 bold>{{ $t(allOptionLabel) }}</LvText>
                    </div>
                  </div>
                </div>
                <div
                  v-for="(topNode, topNodeIndex) in menu"
                  :key="topNode.id"
                  class="classification-list"
                  :class="{ 'pb-12': topNodeIndex === menu.length - 1 }"
                >
                  <LvText h5 bold class="mb-8">{{ $t(topNode.title) }}</LvText>
                  <LvDivider marginTop="0" marginBottom="8px" />
                  <div class="classification-subtitle">
                    <div
                      v-for="node in topNode.items || []"
                      :key="node.id"
                      class="dropdown-menu-item"
                      :class="{ selected: $_isSelected(node) }"
                      @click.stop="$_onSelect(node)"
                    >
                      <LvText h5>{{ $t(node.title) }}</LvText>
                    </div>
                    <LvText
                      v-if="!(topNode.items && topNode.items.length)"
                      h5
                      class="classification-subtitle__empty"
                    >
                      -
                    </LvText>
                  </div>
                </div>
              </perfect-scrollbar>
            </div>
          </transition>
        </div>
      </div>
      <div
        v-if="!noErrorText && errors && errors.length > 0"
        class="lv-dropdown-tree-select__invalid-text"
      >
        <i class="icon-warning lv-dropdown-tree-select__invalid-icon" />
        <p
          v-for="(text, textIndex) in errors"
          :key="textIndex"
          class="lv-dropdown-tree-select__invalid-text-item"
        >
          {{ $t(text) }}
        </p>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import { ValidationProvider } from 'vee-validate';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import H_common from '@/helpers/common';

extend('required', {
  ...required,
  message: '此項目為必填',
});

export default {
  name: 'LvDropdownTreeSelect',
  directives: {
    ClickOutside,
  },
  components: {
    ValidationProvider,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outline: {
      type: Boolean,
      default: true,
    },
    narrow: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '選擇',
    },
    menu: {
      type: Array,
      default() {
        return [];
      },
    },
    value: {
      type: [String, Number],
      default: '',
    },
    scroll: {
      type: Number,
    },
    remindText: {
      type: String,
    },
    remindTextColor: {
      type: String,
      default: 'red',
    },
    remindTextIcon: {
      type: String,
      default: 'icon-warning',
    },
    rules: {
      type: String,
      default: '',
    },
    hasCancelIcon: {
      type: Boolean,
      default: false,
    },
    noErrorText: {
      type: Boolean,
      default: false,
    },
    showAllOption: {
      type: Boolean,
      default: false,
    },
    allOptionId: {
      type: String,
      default: 'all',
    },
    allOptionLabel: {
      type: String,
      default: '全部',
    },
  },
  data() {
    return {
      dropdownMenuShow: false,
      isDropUp: false,
      dropdownHeight: 0,
      dropdownTop: 0,
    };
  },
  computed: {
    _menuNodeMap() {
      const nodeMap = {};
      const traverse = nodes => {
        (nodes || []).forEach(node => {
          nodeMap[node.id] = node;
          traverse(node.items || []);
        });
      };
      traverse(this.menu);
      return nodeMap;
    },
    _isMenuEmpty() {
      const hasMenuItems = (this.menu || []).some(topNode => (topNode.items || []).length > 0);
      return !hasMenuItems && !this.showAllOption;
    },
    _isAllSelected() {
      return this.value === this.allOptionId;
    },
    _isContentDisabled() {
      return this.disabled || this._isMenuEmpty;
    },
    _showText() {
      if (this._isMenuEmpty) {
        return this.$t('無選項');
      }
      if (!this.value) {
        return this.$t(this.placeholder);
      }
      if (this.showAllOption && this.value === this.allOptionId) {
        return this.$t(this.allOptionLabel);
      }
      const node = this._menuNodeMap[this.value];
      return node ? this.$t(node.title) : this.$t(this.placeholder);
    },
    _isDisplayCancelIcon() {
      return this.hasCancelIcon && !this._isContentDisabled && this.value !== '';
    },
  },
  watch: {
    scroll() {
      this.$_getDropdownHeight();
      this.$_isDropUp();
    },
  },
  created() {
    window.addEventListener('resize', this.$_getDropdownHeight);
    window.addEventListener('resize', this.$_isDropUp);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_getDropdownHeight);
    window.removeEventListener('resize', this.$_isDropUp);
  },
  mounted() {
    this.$_getDropdownHeight();
    this.$_isDropUp();
  },
  methods: {
    $_isSelected(node) {
      return node.id === this.value;
    },
    $_onCancel() {
      this.$emit('input', '');
    },
    $_onClick() {
      if (this._isContentDisabled) return;
      this.dropdownMenuShow = !this.dropdownMenuShow;
      this.$_getDropdownHeight();
      this.$_isDropUp();
    },
    $_onCloseMenu() {
      this.dropdownMenuShow = false;
    },
    $_onSelectAll() {
      this.dropdownMenuShow = false;
      this.$emit('input', this.allOptionId);
    },
    $_onSelect(node) {
      this.dropdownMenuShow = false;
      this.$emit('input', node.id);
    },
    $_getDropdownHeight() {
      const { height, y } = H_common.getComponentHeightAndY(this.$refs.LvDropdownTreeSelect);
      this.dropdownHeight = height;
      this.dropdownTop = y;
    },
    $_isDropUp() {
      this.isDropUp = H_common.isDropUp(this.dropdownHeight, this.dropdownTop, 450, 450);
    },
  },
};
</script>

<style lang="scss" scoped>
.lv-dropdown-tree-select {
  width: 100%;
  position: relative;

  &__title {
    margin-bottom: 8px;
  }

  &__content {
    @include ts;
    position: relative;
    border: 1px solid #d9d9d9;
    border-radius: 10px;
    min-height: 46px;
    background: var(--blue-700);

    &.narrow {
      width: 180px;
    }

    &.wide {
      width: 200px;
    }

    .icon {
      color: var(--white);
    }

    &.outline {
      border: solid 1px var(--gray-300);
      background-color: transparent;

      .input-button {
        color: var(--gray-900);
      }

      .icon {
        color: var(--gray-500);
      }

      &:hover {
        border: solid 1px #71b6eb;
        background-color: var(--white);

        .input-button {
          color: var(--blue-500);
        }

        .icon {
          color: var(--blue-500);
        }
      }

      &.lv-dropdown-tree-select__content--error {
        border: solid 1px var(--red-500);
        background-color: rgba(206, 56, 41, 0.1);

        .input-button,
        .icon {
          color: var(--red-500);
        }

        ::v-deep .select.lv-text {
          color: var(--red-500);
        }

        &:hover {
          border: solid 1px var(--red-500);
          background-color: rgba(206, 56, 41, 0.1);

          .input-button,
          .icon {
            color: var(--red-500);
          }
        }
      }
    }

    &.disabled {
      pointer-events: none;
      background: #f5f5f5;

      .icon {
        color: var(--gray-500);
      }
    }

    &--error {
      background-color: rgba(206, 56, 41, 0.1);
      border: solid 1px var(--red-500);

      .input-button,
      .icon {
        color: var(--red-500);
      }

      ::v-deep .select.lv-text {
        color: var(--red-500);
      }
    }
  }

  &__content-wrap {
    display: block;
  }

  &__invalid-text {
    @include flex_align;
    @include auto_margin_top(8px);
  }

  &__invalid-icon {
    font-size: 1rem;
    color: var(--red-500);
  }

  &__invalid-text-item {
    @include auto_margin_left(4px);
    font-size: 0.75rem;
    line-height: 1.5;
    color: var(--red-500);
  }
}

.input-button {
  @include ts;
  width: 100%;
  min-height: 46px;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 36px 0 16px;
  position: relative;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.43;
  letter-spacing: 1px;
  color: var(--white);
}

.select {
  display: block;
  width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  position: absolute;
  color: inherit;
}

.cancel-icon {
  position: absolute;
  right: 30px;
  font-size: 1.5rem;
  color: var(--gray-700);
  pointer-events: auto;

  &:hover {
    color: var(--blue-700);
  }
}

.icon {
  @include ts;
  position: absolute;
  top: 10px;
  font-size: 24px;
  color: var(--white);
  pointer-events: none;

  &.trailing-icon {
    right: 8px;

    &.outline {
      @include tf_scxy(1, -1);

      &.show {
        @include tf_scxy(1, 1);
      }
    }
  }
}

.lv-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  z-index: 300;

  &.drop-up {
    top: initial;
    bottom: calc(100% + 8px);
  }
}

.lv-dropdown-scrollable-content {
  max-height: 320px;
  overflow: hidden;
}

.classification-list {
  padding: 8px 12px 0;
}

.classification-subtitle {
  padding-left: 4px;

  &__empty {
    display: block;
    padding: 10px 16px;
    color: var(--gray-700);
  }

  .dropdown-menu-item {
    @include ts;
    padding: 10px 16px;
    cursor: pointer;
    border-radius: 4px;

    &.all-option {
      padding: 10px 16px 10px 8px;
    }

    &:hover {
      background-color: var(--blue-10);
    }

    &.selected {
      ::v-deep .lv-text {
        color: var(--blue-500);
      }
    }
  }
}
</style>
